import { useState } from "react";
import { Grid, Typography, Checkbox, Box } from "@mui/material";
import LoginHeader from "../Login/LoginHeader";
import ChangeAccount from "../Login/ChangeAccount";
import NewPassword from "../Password/NewPassword";
import PrimaryButton from "../../components-redesign/StyledButtons/PrimaryButton";
import PrivacyPolicy from "./PrivacyPolicy";
import { useLocalTranslationState } from "../../context/AppContext/AppContext";
import { styled } from "@mui/material/styles";
import { myVJBlue200 } from "../../constants/brandingColors";

const FormContainer = styled("form")(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  },
}));

const LoginBtnContainer = styled("div")({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  flexDirection: "column",
});

const TextContainer = styled("div")({
  alignSelf: "stretch",
});

const HeaderContainer = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2.5),
}));

const ChangeAccountLinkContainer = styled("div")(({ theme }) => ({
  paddingBottom: theme.spacing(5),
}));

interface SetPasswordProps {
  username: string;
  password: string;
  setPassword: (password) => void;
  onBack: () => void;
  setMarketingEmails: (checked) => void;
  setCreateAccountState: () => void;
  setAuthError: (username) => void;
}

const SetPassword = ({
  username,
  password,
  setPassword,
  onBack,
  setMarketingEmails,
  setCreateAccountState,
  setAuthError,
}: SetPasswordProps) => {

  const { _translate } = useLocalTranslationState();

  const [isValid, setIsValid] = useState("");
  const [error, setError] = useState("");
  const [marketingEmailsChecked, setMarketingEmailsChecked] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError(_translate("Error, please complete this field"))
      return false
    }
    if (isValid) {
      try {
        setCreateAccountState();
      } catch (error) {
        console.log("Caught error:", error);
        setAuthError(error.message)
      }
    }
  };

  return (
    <>
      <FormContainer noValidate onSubmit={handleSubmit}>
        <TextContainer>
          <Typography variant="subtitle2" fontWeight="400">
            {_translate("Step 2 of 4")}
          </Typography>
        </TextContainer>
        <HeaderContainer>
          <LoginHeader text={_translate("Set your password")} />
        </HeaderContainer>
        <ChangeAccountLinkContainer>
          <ChangeAccount
            username={username}
            onClick={() => {
              setAuthError("")
              onBack()
            }} />
        </ChangeAccountLinkContainer>
        <NewPassword
          password={password}
          label={_translate("Password")}
          setPassword={setPassword}
          error={error}
          setError={setError}
          setIsValid={setIsValid} />
        <Grid
          container
          sx={(theme) => ({
            paddingTop: theme.spacing(4),
          })}
          wrap="nowrap"
        >
          <Grid sx={{ width: '100%' }}>
            <Box display="flex" alignItems="center">
              <Checkbox
                checked={marketingEmailsChecked}
                onChange={(event) => {
                  setMarketingEmailsChecked(event.target.checked);
                  setMarketingEmails(event.target.checked ? 1 : 0)
                }}
                sx={{
                  color: myVJBlue200,
                  padding: 0,
                  marginRight: 1,
                }}
              />
              <Typography variant="subtitle1">{_translate("I would like to receive marketing emails")}</Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container
          sx={(theme) => ({
            paddingTop: theme.spacing(4),
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
          })}
          wrap="nowrap"
          justifyContent="space-between">
          <Grid sx={{ width: '100%' }}>
            <LoginBtnContainer>
              <PrimaryButton
                id="login-password-btn"
                text={_translate("Next")}
                dataTestId="password-btn"
              />
            </LoginBtnContainer>
          </Grid>
        </Grid>

        <PrivacyPolicy />

      </FormContainer>
    </>
  );
};

export default SetPassword;